<template>
  <div class="flex flex-row">
    <MeterGroup :value="loanInfo" class="w-96">
      <template #start>
        <div class="flex justify-content-between h-6 -mb-5 relative font-bold">
          <span
            :style="{ width: loanTotal === 0 ? 0 : Math.min((summaryData.principalBalance / loanTotal) * 100, 100) + '%' }"
            class="absolute text-right transition-all ease-out duration-500"
            >{{ loanTotal === 0 ? 0 : Math.floor((summaryData.principalBalance / loanTotal) * 100) }}%</span
          >
        </div>
      </template>
      <template #meter>
        <span
          :class="meterClass(loanTotal === 0 ? 0 : Math.floor((summaryData.principalBalance / loanTotal) * 100))"
          :style="{ width: loanTotal === 0 ? 0 : Math.min((summaryData.principalBalance / loanTotal) * 100, 100) + '%' }"
        ></span>
      </template>
      <template #label><div></div></template>
      <template #end>
        <LabelField
          label="LOAN BALANCE"
          class="-mt-3 font-bold"
          :display="formatCurrency(summaryData?.principalBalance)"
          :displayClass="summaryData.principalBalance < 0 ? 'text-danger-500' : 'text-success-500'"
        ></LabelField>
        <LabelField
          label="LOAN AVAILABLE"
          class="-mt-3 font-bold"
          :display="formatCurrency(summaryData?.committedAvailable)"
          :displayClass="summaryData.committedAvailable < 0 ? 'text-danger-500' : 'text-success-500'"
        ></LabelField>
      </template>
    </MeterGroup>
    <p class="mt-2 ml-2 font-bold">{{ formatCurrency(loanTotal) }}</p>
  </div>
  <div class="flex flex-row">
    <MeterGroup :value="equityInfo" class="w-96">
      <template #start>
        <div class="flex justify-content-between h-6 -mb-5 relative font-bold">
          <span
            :style="{
              width: equityTotal === 0 ? 0 : Math.min((summaryData.equityBalance / equityTotal) * 100, 100) + '%',
            }"
            class="absolute text-right transition-all ease-out duration-500"
            >{{ equityTotal === 0 ? 0 : Math.floor((summaryData.equityBalance / equityTotal) * 100) }}%</span
          >
        </div>
      </template>
      <template #meter>
        <span
          :class="meterClass(equityTotal === 0 ? 0 : Math.floor((summaryData.equityBalance / equityTotal) * 100))"
          :style="{ width: equityTotal === 0 ? 0 : Math.min((summaryData.equityBalance / equityTotal) * 100, 100) + '%' }"
        ></span>
      </template>
      <template #label><div></div></template>
      <template #end>
        <LabelField
          label="EQUITY BALANCE"
          class="-mt-3 font-bold"
          :display="formatCurrency(summaryData?.equityBalance)"
          :displayClass="summaryData.equityBalance < 0 ? 'text-danger-500' : 'text-success-500'"
        ></LabelField>
        <LabelField
          label="EQUITY AVAILABLE"
          class="-mt-3 font-bold"
          :display="formatCurrency(summaryData?.equityAvailable)"
          :displayClass="summaryData.equityAvailable < 0 ? 'text-danger-500' : 'text-success-500'"
        ></LabelField>
      </template>
    </MeterGroup>
    <p class="mt-2 ml-2 font-bold">{{ formatCurrency(equityTotal) }}</p>
  </div>
</template>

<script setup lang="ts">
import { type MeterItem } from "primevue/metergroup";
import { formatCurrency } from "@/services/Utility";
import type { ProjectDrawInfo } from "@/types/Projects";

interface Props {
  summaryData: ProjectDrawInfo;
}
const props = defineProps<Props>();

const loanTotal = computed<number>(() => props.summaryData.principalBalance + props.summaryData.committedAvailable);

const loanInfo = ref<MeterItem[]>([
  {
    label: "Loan Balance",
    value: loanTotal.value === 0 ? 0 : (props.summaryData.principalBalance / loanTotal.value) * 100,
    color: "",
    icon: "",
  },
]);

const equityTotal = computed<number>(() => props.summaryData.equityBalance + props.summaryData.equityAvailable);
const equityInfo = ref<MeterItem[]>([
  {
    label: "Equity Balance",
    value: equityTotal.value === 0 ? 0 : (props.summaryData.equityBalance / equityTotal.value) * 100,
    color: "",
    icon: "",
  },
]);

const meterClass = (value: number): string => {
  if (value < 50) return "rounded-lg bg-success-500 transition-all ease-out duration-500";
  else if (value <= 100) return "rounded-lg bg-yellow-500 transition-all ease-out duration-500";
  else return "rounded-lg bg-danger-500 transition-all ease-out duration-500";
};
</script>
